import { Box, List, ListItemButton } from "@mui/material";
import React, { useEffect, useState } from "react";
import { createScopedLogger } from "../../../common";
import LoadingOverlay from "../../../common/components/LoadingOverlay";
import { useErrorHandler } from "../../../common/hooks";
import { VariableNames } from "../../Constants/VariableNames";
import { useChildContexts } from "../../Hooks/useChildContexts";
import { getContainerClasses, useComponentSelection } from "../../Hooks/useComponentSelection";
import useObservedExpression from "../../Hooks/useObservedExpression";
import { ListSelectModel } from "../../Models/ListSelectModel";
import { IsDesignMode } from "../../Utils/runtimeDetection";
import ComponentFactory from "../ComponentFactory";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";

const logger = createScopedLogger("ListSelectComponent");

const ListSelectComponent: React.FC<IComponentProps<ListSelectModel>> = ({ component, context, componentPath = [] }) => {
  const errorHandler = useErrorHandler();
  const componentLayoutSx = getComponentLayout(component);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const [items, itemsPending] = useObservedExpression<any[]>(component.Items, context, "ListSelectComponent", (value, error) => {
    if (error || value === undefined || value === null || value === false) {
      return [];
    }

    if (!Array.isArray(value)) {
      logger.error("Items expression did not return an array:", value);
      throw new Error("Items expression did not return an array: " + value);
    }
    return value;
  });

  // Observe the Item variable to keep selection in sync
  const [currentItem] = useObservedExpression<any>(component.Item, context, "ListSelectComponent.Item");

  // Sync selectedIndex with currentItem value (only if Item binding exists)
  useEffect(() => {
    // Only sync selection from Item variable if it's bound
    if (!component.Item) {
      return;
    }

    if (!items || items.length === 0) {
      setSelectedIndex(null);
      return;
    }

    if (currentItem === undefined || currentItem === null) {
      setSelectedIndex(null);
      return;
    }

    // Find the index of the current item in the items array
    const index = items.findIndex((item) => {
      const itemKey = item[component.ItemKeyField];
      const currentItemKey = currentItem[component.ItemKeyField];
      return itemKey === currentItemKey;
    });

    setSelectedIndex(index >= 0 ? index : null);
  }, [currentItem, items, component.ItemKeyField, component.Item]);

  // Create child contexts for each item (cached by key)
  const contextItems = useChildContexts(context, items, component.ItemKeyField, VariableNames.Item);

  // Apply data attributes to root for designer detection
  const outerSelectionProps = useComponentSelection({
    component,
    componentPath,
    isContainer: true,
    isEmpty: !items || items.length === 0,
  });

  // Get visual classes for the children container
  const innerContainerClassName = getContainerClasses(IsDesignMode, !component.Children || component.Children.length === 0);

  const handleItemClick = async (item: any, index: number) => {
    // Only set selection immediately if Item variable is not bound
    // If Item is bound, let the useEffect sync it after assignment
    if (!component.Item) {
      setSelectedIndex(index);
    }

    try {
      // Assign the selected item to the Item variable if specified
      if (component.Item) {
        await context.Evaluator.assign(component.Item, context, item);
      }

      // Evaluate the OnItemSelected expression if specified
      if (component.OnItemSelected) {
        const eventContext = context.CreateChild();
        eventContext.VariableScope.set(VariableNames.Item, item);
        eventContext.VariableScope.set(VariableNames.Event, item);
        await eventContext.Evaluator.evaluate(component.OnItemSelected, eventContext);
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  return (
    <Box sx={{ position: "relative", ...componentLayoutSx }} {...outerSelectionProps}>
      <LoadingOverlay loading={itemsPending} />
      <List>
        {contextItems?.map(({ key, context: itemContext }, index) => {
          const item = items?.[index];
          return (
            <ListItemButton key={key} selected={selectedIndex === index} onClick={() => handleItemClick(item, index)}>
              <Box sx={{ width: "100%" }} className={innerContainerClassName}>
                {component.Children.map((child) => (
                  <ComponentFactory parent={component} key={child.Id} component={child} context={itemContext} componentPath={[...componentPath, child.Id]} />
                ))}
              </Box>
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
};

export default ListSelectComponent;
